import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BookOpen, CheckCircle2, ShieldCheck, Clock } from "lucide-react";

export default function ProgramDetail() {
    const { id } = useParams();
    const [program, setProgram] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(false);
    const [enrolled, setEnrolled] = useState(false);
    const [residency, setResidency] = useState("domestic");
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/programs/${id}`).then((r) => setProgram(r.data)).finally(() => setLoading(false));
        if (user) {
            api.get("/me/enrollments").then((r) => {
                setEnrolled(r.data.some((e) => e.program_id === id));
            });
            // Pre-select residency from the user's profile if they've chosen
            // one before — purely for continuity; they can still switch on
            // this page.
            if (user.residency === "international" || user.residency === "domestic") {
                setResidency(user.residency);
            }
        }
    }, [id, user]);

    const handleEnroll = async () => {
        setBuying(true);
        try {
            // Payment gate — refuse checkout until the student has submitted
            // their admission application.
            try {
                const status = await api.get("/me/application-status");
                if (!status.data?.has_submitted_application) {
                    toast.error(
                        "Please complete your admission application before paying — redirecting…",
                        { duration: 4000 },
                    );
                    setBuying(false);
                    setTimeout(() => {
                        window.location.href = `/apply?program=${id}`;
                    }, 1200);
                    return;
                }
            } catch (e) {
                // 401 (not logged in) — let the existing flow show the login prompt.
                if (e?.response?.status !== 401) {
                    toast.error("Could not verify application status.");
                    setBuying(false);
                    return;
                }
            }
            const { data } = await api.post("/checkout/session", {
                program_id: id,
                origin_url: window.location.origin,
                residency,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not start checkout. " + (e?.response?.data?.detail || ""));
            setBuying(false);
        }
    };

    if (loading)
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
                Loading program…
            </div>
        );
    if (!program)
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
                Program not found
            </div>
        );

    return (
        <div className="max-w-7xl mx-auto px-6 py-12" data-testid="program-detail-page">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
                <div>
                    <div className="aspect-[16/9] bg-muted overflow-hidden rounded-sm border border-border mb-8">
                        <img
                            src={program.thumbnail_url}
                            alt={program.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex items-center gap-2 mb-4">
                        <Badge variant="outline" className="capitalize">
                            {program.subject}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                            {program.level}
                        </Badge>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl text-primary leading-tight">
                        {program.title}
                    </h1>
                    <p className="mt-6 text-lg text-foreground/75 leading-relaxed">
                        {program.description}
                    </p>

                    <div className="mt-12">
                        <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">
                            Curriculum
                        </div>
                        <div className="space-y-3">
                            {(program.chapters || []).map((ch, i) => (
                                <Card key={ch.id} className="border-border">
                                    <CardContent className="p-5 flex items-start gap-4">
                                        <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center shrink-0">
                                            <span className="font-display text-primary">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-display text-lg text-primary">
                                                {ch.title}
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {ch.description}
                                            </p>
                                            <div className="text-xs text-muted-foreground mt-2 flex gap-4">
                                                <span>{ch.lessons?.length || 0} lessons</span>
                                                {ch.has_quiz && (
                                                    <span>
                                                        {ch.quiz_question_count}-question quiz
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                <aside className="lg:sticky lg:top-24 self-start" data-testid="enroll-panel">
                    <Card className="border-border">
                        <CardContent className="p-6">
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                                Tuition
                            </div>
                            {(() => {
                                const hasIntl =
                                    program.price_international != null
                                    && Number(program.price_international) > 0;
                                const active = hasIntl ? residency : "domestic";
                                const displayed =
                                    active === "international" && hasIntl
                                        ? Number(program.price_international)
                                        : Number(program.price || 0);
                                return (
                                    <>
                                        <div className="font-display text-4xl text-primary mt-1">
                                            ${displayed.toFixed(2)}
                                            <span className="text-base text-muted-foreground ml-1.5 align-middle">
                                                CAD
                                            </span>
                                        </div>
                                        <div className="text-sm text-muted-foreground mt-1">
                                            One-time payment · Lifetime access
                                        </div>
                                        {hasIntl && !enrolled ? (
                                            <div
                                                className="mt-4 rounded-md border border-border bg-muted/20 p-2"
                                                data-testid="residency-toggle"
                                            >
                                                <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 px-1">
                                                    I am a
                                                </div>
                                                <div className="grid grid-cols-2 gap-1">
                                                    {[
                                                        {
                                                            key: "domestic",
                                                            label: "Domestic",
                                                            sub: `$${Number(program.price).toFixed(2)}`,
                                                        },
                                                        {
                                                            key: "international",
                                                            label: "International",
                                                            sub: `$${Number(program.price_international).toFixed(2)}`,
                                                        },
                                                    ].map((opt) => {
                                                        const on = residency === opt.key;
                                                        return (
                                                            <button
                                                                key={opt.key}
                                                                type="button"
                                                                onClick={() => setResidency(opt.key)}
                                                                className={`flex flex-col items-start rounded-md px-3 py-2 text-left transition-colors ${
                                                                    on
                                                                        ? "bg-primary text-primary-foreground"
                                                                        : "bg-background hover:bg-muted"
                                                                }`}
                                                                data-testid={`residency-${opt.key}`}
                                                            >
                                                                <span className="text-xs font-semibold">
                                                                    {opt.label}
                                                                </span>
                                                                <span className={`text-[11px] ${on ? "opacity-90" : "text-muted-foreground"}`}>
                                                                    {opt.sub} CAD
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : null}
                                    </>
                                );
                            })()}

                            {enrolled ? (
                                <Button
                                    className="w-full mt-6 bg-primary hover:bg-primary/90"
                                    onClick={() => navigate(`/learn/${program.id}`)}
                                    data-testid="continue-learning-button"
                                >
                                    Continue Learning
                                </Button>
                            ) : (
                                <Button
                                    className="w-full mt-6 bg-primary hover:bg-primary/90"
                                    onClick={handleEnroll}
                                    disabled={buying}
                                    data-testid="enroll-program-button"
                                >
                                    {buying ? "Starting checkout…" : "Enroll now"}
                                </Button>
                            )}

                            <div className="mt-6 space-y-3 pt-6 border-t border-border">
                                {[
                                    { icon: BookOpen, t: `${program.chapters?.length || 0} chapters` },
                                    {
                                        icon: Clock,
                                        t: `${(program.total_hours ?? (program.chapters || []).reduce((s, c) => s + (Number(c.hours) || 1), 0)).toFixed(1)} total hours`,
                                    },
                                    { icon: CheckCircle2, t: "20-question quiz per chapter" },
                                    { icon: ShieldCheck, t: "Branded certificate on completion" },
                                ].map((f, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-sm">
                                        <f.icon className="h-4 w-4 text-accent" />
                                        <span className="text-foreground/80">{f.t}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </aside>
            </div>
        </div>
    );
}
